import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Check, ArrowRight, Lock, Sparkles, ShieldCheck, X } from "lucide-react";
import { useI18n } from "../i18n";
import { useAuth } from "../AuthContext";
import api, { fmtErr } from "../api";
import EnterpriseModal from "./EnterpriseModal";

export default function Pricing() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const [loadingTier, setLoadingTier] = useState(null);
  const [err, setErr] = useState("");
  const [stats, setStats] = useState(null);
  const [entOpen, setEntOpen] = useState(false);

  useEffect(() => {
    api.get("/public/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  async function startCheckout(tier) {
    if (!user) {
      nav(`/register?next=${encodeURIComponent("/pricing")}`);
      return;
    }
    setLoadingTier(tier); setErr("");
    try {
      const { data } = await api.post("/subscriptions/checkout", { tier, origin_url: window.location.origin });
      const url = data?.url;
      if (url) {
        // Navigate the TOP window. When the app is rendered inside an
        // iframe (mobile preview / webview), `window.location.href = url`
        // only changes the iframe, not the parent — Stripe Checkout never
        // opens. Falling back to the current frame keeps the desktop /
        // non-iframe path working.
        try {
          if (window.top && window.top !== window.self) {
            window.top.location.href = url;
            return;
          }
        } catch (_) {
          /* Cross-origin frame access — fall through to current frame. */
        }
        window.location.assign(url);
        return;
      }
      // Backend returned 200 but no url — surface a clear error instead of
      // leaving the button stuck in the "…" loading state.
      setErr("Checkout could not be started. Please try again.");
      setLoadingTier(null);
    } catch (e) {
      setErr(fmtErr(e.response?.data?.detail) || e.message);
      setLoadingTier(null);
    }
  }

  const currentTier = user?.subscription_tier || "free";
  const spotsLeft = stats?.founding_spots_remaining ?? 10;

  const copy = {
    en: {
      kicker: "PLANS · CORRIDOR DIGITAL",
      title: "Pricing built for the corridor.",
      sub: "Cancel anytime. Bilingual ES/EN included on every tier. 60-day onboarding free for in-person signups.",
      foundingBar: `Only ${spotsLeft} of 10 founding spots available — lock $149/mo for life`,
      foundingCta: "See founding offer →",
      anchor: "Most plans paid annually save 20%. Founding members lock the lowest price for life.",
      most_popular: "MOST POPULAR",
      best_value: "BEST VALUE",
      enterprise_tag: "$85K+ engagement",
      tiers: {
        free: {
          name: "Listed", tag: "Free", price: "$0", per: "/ month",
          features: [
            ["Public bilingual profile (basic)", true],
            ["Listed in corridor directory", true],
            ["1 photo, basic contact", true],
            ["Receive RFQ inquiries through platform", false],
          ],
          cta: "Get listed free",
        },
        pro: {
          name: "Pro", tag: "Corridor Verified", price: "$199", per: "/ month",
          striked: null,
          features: [
            ["Full bilingual microsite", true],
            ["Document vault (unlimited)", true],
            ["Secure share links + view tracking", true],
            ["Inquiry inbox + RFQ routing", true],
            ["Expiration alerts (USMCA, IMMEX, ISO)", true],
            ["Corridor Verified — via evidence review", true, "contact"],
            ["AI-powered ES↔EN translation", true],
            ["Cross-Border Data Bridge", false],
            ["Featured in monthly Supplier Digest", false],
          ],
          cta: "Upgrade to Pro",
          subtext: "60-day money-back guarantee",
        },
        premium: {
          name: "Premium", tag: "Featured Supplier", price: "$599", per: "/ month",
          striked: null,
          features: [
            ["Everything in Pro, plus:", true],
            ["Cross-Border Data Bridge (US buyer portals)", true],
            ["Featured in monthly Supplier Digest", true, "soon"],
            ["Top of directory in your category", true],
            ["Lead routing to phone + WhatsApp", true],
            ["Quarterly analytics report", true, "soon"],
            ["Priority support", true],
          ],
          cta: "Upgrade to Premium",
          subtext: "Most chosen by corridor manufacturers",
        },
        enterprise: {
          name: "IMMEX 4.0", tag: "Enterprise", price: "Custom", per: "$85K+ engagement",
          features: [
            ["Custom bilingual build + domain SEO", true],
            ["Dedicated Cross-Border Data Bridge", true],
            ["ERP / compliance integrations", true],
            ["White-glove onboarding + onsite visit", true],
            ["Monthly strategy retainer ($8K–$25K)", true],
            ["Direct Founder access", true],
          ],
          cta: "Request strategy call",
          subtext: "By invitation. We respond within 24h.",
        },
      },
      currentL: "★ CURRENT",
      guarantee: "60-DAY MONEY-BACK GUARANTEE — IF WE DON'T DRIVE ONE QUALIFIED US INQUIRY, FULL REFUND.",
    },
    es: {
      kicker: "PLANES · CORRIDOR DIGITAL",
      title: "Precios hechos para el corredor.",
      sub: "Cancela cuando quieras. ES/EN bilingüe en todos los planes. 60 días de onboarding gratis para registros en persona.",
      foundingBar: `Solo ${spotsLeft} de 10 lugares disponibles — fija $149/mes de por vida`,
      foundingCta: "Ver oferta fundador →",
      anchor: "Pago anual ahorra 20%. Fundadores fijan el precio más bajo de por vida.",
      most_popular: "MÁS POPULAR",
      best_value: "MEJOR VALOR",
      enterprise_tag: "$85K+ por engagement",
      tiers: {
        free: {
          name: "Listado", tag: "Gratis", price: "$0", per: "/ mes",
          features: [
            ["Perfil bilingüe público (básico)", true],
            ["Listado en directorio del corredor", true],
            ["1 foto, contacto básico", true],
            ["Recibir cotizaciones por la plataforma", false],
          ],
          cta: "Listar gratis",
        },
        pro: {
          name: "Pro", tag: "Corredor Verificado", price: "$199", per: "/ mes",
          features: [
            ["Micrositio bilingüe completo", true],
            ["Bóveda de documentos (ilimitada)", true],
            ["Enlaces seguros + rastreo", true],
            ["Bandeja de cotizaciones + ruteo RFQ", true],
            ["Alertas vencimiento (USMCA, IMMEX, ISO)", true],
            ["Insignia Corredor Verificado — vía revisión de evidencia", true, "contact"],
            ["Traducción ES↔EN con IA", true],
            ["Cross-Border Data Bridge", false],
            ["Destacado en Supplier Digest mensual", false],
          ],
          cta: "Mejorar a Pro",
          subtext: "Garantía de 60 días o devolvemos tu dinero",
        },
        premium: {
          name: "Premium", tag: "Proveedor Destacado", price: "$599", per: "/ mes",
          features: [
            ["Todo lo de Pro, más:", true],
            ["Cross-Border Data Bridge (portales para compradores)", true],
            ["Destacado en Supplier Digest mensual", true, "soon"],
            ["Tope de directorio en tu categoría", true],
            ["Ruteo de leads a teléfono + WhatsApp", true],
            ["Reporte trimestral de analítica", true, "soon"],
            ["Soporte prioritario", true],
          ],
          cta: "Mejorar a Premium",
          subtext: "El más elegido por fabricantes del corredor",
        },
        enterprise: {
          name: "IMMEX 4.0", tag: "Enterprise", price: "Custom", per: "$85K+ por engagement",
          features: [
            ["Construcción bilingüe a medida + SEO dominio", true],
            ["Cross-Border Data Bridge dedicado", true],
            ["Integración ERP / cumplimiento", true],
            ["Onboarding de alto contacto + visita en planta", true],
            ["Retainer mensual de estrategia ($8K–$25K)", true],
            ["Acceso directo al Fundador", true],
          ],
          cta: "Solicitar llamada estratégica",
          subtext: "Por invitación. Respondemos en 24h.",
        },
      },
      currentL: "★ ACTUAL",
      guarantee: "GARANTÍA DE 60 DÍAS — SI NO TRAEMOS UNA COTIZACIÓN CALIFICADA DE EE.UU., REEMBOLSO COMPLETO.",
    },
  };
  const c = copy[lang];
  const tiers = c.tiers;

  return (
    <div data-testid="pricing-page">
      {/* Scarcity bar */}
      <div className="text-center py-3 font-mono text-xs" style={{ background: "var(--orange)", color: "white" }} data-testid="founding-scarcity-bar">
        <Lock size={11} className="inline mr-2 -mt-1" /> {c.foundingBar}
        <Link to="/founding" className="underline ml-3 font-semibold">{c.foundingCta}</Link>
      </div>

      <section className="container-cd py-16">
        <div className="overline">{c.kicker}</div>
        <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[0.9] max-w-4xl">{c.title}</h1>
        <p className="mt-6 text-lg max-w-2xl" style={{ color: "var(--ink-2)" }}>{c.sub}</p>
        <div className="mt-6">
          <Link
            to="/roi"
            className="inline-flex items-center gap-2 cd-chip cd-chip-interactive font-mono text-xs"
            data-testid="pricing-roi-link"
          >
            {lang === "es"
              ? "¿Eres comprador en EE.UU.? Calcula tu ahorro al nearshorear →"
              : "Are you a US buyer? Calculate your nearshoring savings →"}
          </Link>
        </div>
        {err ? <div className="mt-6 font-mono text-xs px-3 py-2" style={{ background: "var(--red)", color: "white" }} data-testid="pricing-error">{err}</div> : null}

        {/* 4-tier grid */}
        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-0" style={{ border: "1px solid var(--line)" }}>
          <TierCol
            tier="free" {...tiers.free}
            currentL={c.currentL}
            current={currentTier === "free" && !!user}
            onClick={() => (user ? null : nav("/register"))}
            disabled={!!user && currentTier === "free"}
            data-testid="tier-free"
          />
          <TierCol
            tier="pro" {...tiers.pro}
            currentL={c.currentL}
            highlight
            badge={c.most_popular}
            current={currentTier === "pro"}
            loading={loadingTier === "pro"}
            onClick={() => startCheckout("pro")}
            disabled={currentTier === "pro"}
            data-testid="tier-pro"
          />
          <TierCol
            tier="premium" {...tiers.premium}
            currentL={c.currentL}
            badge={c.best_value}
            current={currentTier === "premium"}
            loading={loadingTier === "premium"}
            onClick={() => startCheckout("premium")}
            disabled={currentTier === "premium"}
            data-testid="tier-premium"
          />
          <TierCol
            tier="enterprise" {...tiers.enterprise}
            currentL={c.currentL}
            dark
            onClick={() => setEntOpen(true)}
            data-testid="tier-enterprise"
            last
          />
        </div>

        {/* Risk reversal */}
        <div className="mt-10 cd-card p-6 flex items-center gap-4" style={{ borderColor: "var(--orange)" }}>
          <ShieldCheck size={28} style={{ color: "var(--orange)" }} />
          <div className="font-mono text-xs md:text-sm tracking-wider">{c.guarantee}</div>
        </div>

        <div className="mt-6 font-mono text-xs text-center" style={{ color: "var(--ink-2)" }}>
          {c.anchor}
        </div>
      </section>

      {entOpen && <EnterpriseModal onClose={() => setEntOpen(false)} lang={lang} />}
    </div>
  );
}

function TierCol({
  tier, name, tag, price, per, features, cta, subtext, currentL, current, disabled, loading, onClick, highlight, dark, badge, last,
}) {
  const { lang } = useI18n();
  const isEs = lang === "es";
  const bg = dark ? "var(--ink)" : highlight ? "var(--orange)" : "white";
  const fg = dark || highlight ? "white" : "var(--ink)";
  const muted = dark || highlight ? "rgba(255,255,255,0.7)" : "var(--ink-2)";
  return (
    <div
      className="p-8 flex flex-col relative"
      style={{ background: bg, color: fg, borderRight: last ? "none" : "1px solid var(--line)" }}
      data-testid={`pricing-col-${tier}`}
    >
      {badge && (
        <div className="absolute top-0 right-0 px-3 py-1 font-mono text-[10px] tracking-wider"
             style={{ background: dark ? "var(--orange)" : "var(--ink)", color: "white" }}>
          {badge}
        </div>
      )}
      <div className="overline" style={{ color: muted }}>{tag}</div>
      <div className="font-display text-3xl mt-3">{name}</div>
      <div className="font-display text-5xl mt-6">
        {price}
        <span className="font-body text-sm font-normal" style={{ color: muted }}> {per}</span>
      </div>
      {current && (
        <div className="mt-3 overline" style={{ color: dark || highlight ? "white" : "var(--orange)" }}>
          {currentL}
        </div>
      )}
      <ul className="mt-8 space-y-3 flex-1">
        {features.map(([f, included, marker], i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            {included
              ? <Check size={16} className="mt-0.5 flex-shrink-0" />
              : <X size={16} className="mt-0.5 flex-shrink-0" style={{ color: muted, opacity: 0.5 }} />}
            <span style={{ color: included ? fg : muted, textDecoration: included ? "none" : "line-through", opacity: included ? 1 : 0.6 }}>
              {f}
              {marker ? (
                <span
                  className="font-mono uppercase inline-block align-middle"
                  style={{
                    fontSize: 9,
                    letterSpacing: "0.08em",
                    padding: "2px 6px",
                    marginLeft: 6,
                    borderRadius: 2,
                    whiteSpace: "nowrap",
                    background: dark || highlight ? "rgba(255,255,255,0.18)" : marker === "soon" ? "#fef3c7" : "#f1f5f9",
                    color: dark || highlight ? "white" : marker === "soon" ? "#92400e" : "#334155",
                    border: dark || highlight ? "1px solid rgba(255,255,255,0.35)" : marker === "soon" ? "1px solid #fcd34d" : "1px solid #cbd5e1",
                  }}
                  data-testid={`pricing-marker-${tier}-${marker}-${i}`}
                >
                  {marker === "soon"
                    ? (isEs ? "Próximamente" : "Coming soon")
                    : (isEs ? "Contactar a Corridor Digital" : "Contact Corridor Digital")}
                </span>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {subtext && (
        <div className="mt-6 font-mono text-xs" style={{ color: muted }}>{subtext}</div>
      )}
      <button
        type="button"
        onClick={onClick}
        className="cd-cta-btn-primary mt-6 w-full"
        disabled={disabled || loading}
        data-testid={`pricing-cta-${tier}`}
      >
        {loading ? "…" : disabled ? currentL : cta}
        {!disabled && !loading ? <ArrowRight size={16} /> : null}
      </button>
    </div>
  );
}
